import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { TicketErrorModal } from '../../NewSeatedPurchaseWrapper/providers/CheckoutProvider/TicketErrorModal'
import {
  placeGATicketsIntoCart,
  processFreeOrder,
  isWaitingRoomRequiredError,
} from '../../../../utilities/api'
import { redirectToWaitingRoom } from '../../../../utilities/waitingRoom'
import { trackAddToCart } from '../../../../utilities/tracking'
import { storeCartSession } from '../../../../utilities/cartSessionTracking'
import { readTrackingCode } from '../../../../utilities/trackingLink'
import authService from '../../../../utilities/services/auth.service'

const navigateToComplimentarySuccess = (
  navigate,
  processRes,
  setCheckingAvailability
) => {
  sessionStorage.setItem('order', JSON.stringify({ id: processRes.data?.id }))
  navigate(
    `/checkout/checkout-success?intentId=${encodeURIComponent(processRes.data?.paymentIntentId)}`
  )
  setCheckingAvailability(false)
}

// Create the context
const GACheckoutContext = createContext(undefined)

export const useGACheckout = () => {
  const context = useContext(GACheckoutContext)
  if (!context) {
    throw new Error('Context must be used within a CheckoutProvider')
  }
  return context
}

// Provide all functionality + modal for proceeding to checkout
export const GACheckoutProvider = ({
  selectedFromQuickpicks,
  event,
  children,
}) => {
  const dialogRef = useRef(null)
  const navigate = useNavigate()

  const [seatedTickets, setSeatedTickets] = useState()
  const [seatedError, setSeatedError] = useState(null)
  const [checkingAvailability, setCheckingAvailability] = useState(false)

  useEffect(() => {
    if (seatedTickets && seatedTickets.success) {
      navigate(`/checkout?eventId=${event.uuid}`)
    }
  }, [seatedTickets])

  useEffect(() => {
    if (seatedError) {
      dialogRef.current.showModal()
    }
  }, [seatedError])

  // This is only valid for a single ticket
  const checkout = () => {
    setCheckingAvailability(true)
    let data = {
      ticketGroup: selectedFromQuickpicks,
      eventId: event.id,
    }

    console.log('placeGATicketsIntoCart data: ', data)
    placeGATicketsIntoCart(data)
      .then((res) => {
        trackAddToCart({
          organization: event?.organization,
          cart: res.data,
        })
        storeCartSession({
          cartId: res.data.cartId,
          sessionId: res.data.sessionId,
        })
        sessionStorage.setItem(
          'cart',
          JSON.stringify({ cartId: res.data.cartId })
        )
        if (res.data.freeOffer) {
          if (!authService.isLoggedIn()) {
            setCheckingAvailability(false)
            navigate('/checkout')
            return
          }
          processFreeOrder({
            cartId: res.data.cartId,
            trackingCode: readTrackingCode(event?.uuid),
          })
            .then((processRes) =>
              navigateToComplimentarySuccess(
                navigate,
                processRes,
                setCheckingAvailability
              )
            )
            .catch((err) => {
              setCheckingAvailability(false)
              if (err.response) {
                setSeatedError({
                  status: err.response.status,
                  title: 'Checkout unavailable',
                  message:
                    err.response.data?.error?.message ||
                    'Unable to complete complimentary checkout.',
                  buttonText: 'Return to tickets list',
                })
              } else {
                setSeatedError({
                  status: 500,
                  title: 'Checkout unavailable',
                  message: 'Network error occurred',
                  buttonText: 'Return to tickets list',
                })
              }
            })
          return
        }
        setCheckingAvailability(false)
        setSeatedTickets(res.data)
      })
      .catch((err) => {
        setCheckingAvailability(false)
        if (isWaitingRoomRequiredError(err) && event?.uuid) {
          redirectToWaitingRoom(
            navigate,
            event.seoUrl,
            event.shortCode,
            event.uuid,
            'ga'
          )
          return
        }
        if (err.response) {
          setSeatedError({
            status: err.response.status,
            title: 'Selected tickets not available',
            message: err.response.data.error.message || 'An error occurred',
            buttonText: 'Return to tickets list',
          })
        } else {
          setSeatedError({
            status: 500,
            title: 'Selected tickets not available',
            message: 'Network error occurred',
            buttonText: 'Return to tickets list',
          })
        }
      })
  }

  const resetSeatedError = () => {
    setSeatedError(null)
  }

  const modalClose = () => {
    resetSeatedError()
    dialogRef.current.close()
    // Probably just want to refresh here if tickets not available
  }

  const openTicketErrorModal = (title, message) => {
    setSeatedError({
      status: 500,
      title: title,
      message: message,
      buttonText: 'Got it',
    })
  }

  return (
    <GACheckoutContext.Provider
      value={{
        checkout,
        checkingAvailability,
        event,
        openTicketErrorModal,
      }}
    >
      {children}
      <TicketErrorModal
        dialogRef={dialogRef}
        seatedError={seatedError}
        handleClose={modalClose}
      />
    </GACheckoutContext.Provider>
  )
}
